import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Slider,
  Stack,
  Chip,
  Card,
  CardContent,
  Grid,
  Switch,
  FormControlLabel,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import {
  Brightness3,
  Public,
  Timeline,
  LocationOn,
  Refresh
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Polygon, Rectangle, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import SunCalc from 'suncalc';
import moment from 'moment';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const defaultCenter = [39.8283, -98.5795]; // Geographic center of contiguous US

// Component to handle map clicks
function MapClickHandler({ onMapClick }) {
  useMapEvents({
    click: (e) => {
      onMapClick(e.latlng);
    }
  });
  return null;
}

// Moon phase colors
const PHASE_COLORS = {
  'New Moon': '#1a1a2e',
  'Waxing Crescent': '#2d3250',
  'First Quarter': '#424769',
  'Waxing Gibbous': '#7077a1',
  'Full Moon': '#f6b93b',
  'Waning Gibbous': '#7077a1',
  'Last Quarter': '#424769',
  'Waning Crescent': '#2d3250'
};

function MoonPosition() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [moonData, setMoonData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [resolution, setResolution] = useState('medium');
  const [showGrid, setShowGrid] = useState(true);
  const [showZones, setShowZones] = useState(true);
  const [viewMode, setViewMode] = useState('heatmap'); // heatmap, zones, markers
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationData, setLocationData] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [hoursOffset, setHoursOffset] = useState(0); // Hours into the future (0-48)

  // Helper function to get moon phase name
  const getMoonPhaseName = useCallback((phase) => {
    if (phase < 0.033) return 'New Moon';
    if (phase < 0.216) return 'Waxing Crescent';
    if (phase < 0.283) return 'First Quarter';
    if (phase < 0.466) return 'Waxing Gibbous';
    if (phase < 0.533) return 'Full Moon';
    if (phase < 0.716) return 'Waning Gibbous';
    if (phase < 0.783) return 'Last Quarter';
    if (phase < 0.966) return 'Waning Crescent';
    return 'New Moon';
  }, []);

  // Calculate moon position grid data
  const calculateMoonData = useCallback(() => {
    setLoading(true);
    setError(null);

    try {
      const targetDate = selectedDate;
      
      // Define US bounds (continental US)
      const bounds = {
        north: 49.0,
        south: 25.0,
        east: -66.0,
        west: -125.0
      };
      
      // Resolution settings (number of grid points)
      const resolutions = {
        low: { lat: 25, lng: 60 },      // ~150 points
        medium: { lat: 50, lng: 120 },  // ~600 points  
        high: { lat: 100, lng: 240 },   // ~2400 points
        ultra: { lat: 200, lng: 480 }   // ~9600 points
      };
      
      const gridRes = resolutions[resolution] || resolutions.medium;
      
      // Calculate step size
      const latStep = (bounds.north - bounds.south) / gridRes.lat;
      const lngStep = (bounds.east - bounds.west) / gridRes.lng;
      
      const gridData = [];
      
      // Generate grid points
      for (let lat = bounds.south; lat <= bounds.north; lat += latStep) {
        for (let lng = bounds.west; lng <= bounds.east; lng += lngStep) {
          const moonPos = SunCalc.getMoonPosition(targetDate, lat, lng);
          const moonIllum = SunCalc.getMoonIllumination(targetDate);
          
          // Convert altitude (elevation angle) from radians to degrees
          const altitudeDeg = moonPos.altitude * (180 / Math.PI);
          
          // Convert azimuth from radians to degrees
          const azimuthDeg = moonPos.azimuth * (180 / Math.PI);
          
          // Only include points where moon is above horizon
          if (altitudeDeg > 0) {
            gridData.push({
              lat,
              lng,
              altitude: altitudeDeg,
              azimuth: azimuthDeg,
              distance: moonPos.distance,
              parallacticAngle: moonPos.parallacticAngle * (180 / Math.PI),
              fraction: moonIllum.fraction,
              phase: moonIllum.phase,
              angle: moonIllum.angle * (180 / Math.PI)
            });
          }
        }
      }

      const moonIllum = SunCalc.getMoonIllumination(targetDate);
      
      setMoonData({
        date: targetDate.toISOString(),
        resolution,
        bounds,
        pointCount: gridData.length,
        moonPhase: getMoonPhaseName(moonIllum.phase),
        illumination: moonIllum,
        gridData
      });
    } catch (err) {
      console.error('Error calculating moon data:', err);
      setError(err.message || 'Failed to calculate moon position data');
    } finally {
      setLoading(false);
    }
  }, [selectedDate, resolution, getMoonPhaseName]);

  const calculateLocationData = useCallback((lat, lng) => {
    try {
      const targetDate = selectedDate;
      const latitude = parseFloat(lat);
      const longitude = parseFloat(lng);
      
      // Get moon position
      const moonPos = SunCalc.getMoonPosition(targetDate, latitude, longitude);
      const moonIllum = SunCalc.getMoonIllumination(targetDate);
      
      // Get moon rise/set times
      const moonTimes = SunCalc.getMoonTimes(targetDate, latitude, longitude);
      
      setLocationData({
        date: targetDate.toISOString(),
        location: { lat: latitude, lng: longitude },
        position: {
          altitude: moonPos.altitude * (180 / Math.PI),
          azimuth: moonPos.azimuth * (180 / Math.PI),
          distance: moonPos.distance,
          parallacticAngle: moonPos.parallacticAngle * (180 / Math.PI)
        },
        illumination: {
          fraction: moonIllum.fraction,
          phase: moonIllum.phase,
          angle: moonIllum.angle * (180 / Math.PI),
          phaseName: getMoonPhaseName(moonIllum.phase)
        },
        times: {
          rise: moonTimes.rise ? moonTimes.rise.toISOString() : null,
          set: moonTimes.set ? moonTimes.set.toISOString() : null,
          alwaysUp: moonTimes.alwaysUp || false,
          alwaysDown: moonTimes.alwaysDown || false
        }
      });
    } catch (err) {
      console.error('Error calculating location data:', err);
    }
  }, [selectedDate, getMoonPhaseName]);

  useEffect(() => {
    calculateMoonData();
  }, [calculateMoonData]);

  // Auto-refresh every minute when enabled
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      setSelectedDate(new Date());
    }, 60000);

    return () => clearInterval(interval);
  }, [autoRefresh]);

  const handleMapClick = useCallback((latlng) => {
    const lat = latlng.lat;
    const lng = latlng.lng;
    setSelectedLocation({ lat, lng });
    calculateLocationData(lat, lng);
  }, [calculateLocationData]);


  const getAltitudeColor = (altitude) => {
    // Color gradient based on moon altitude
    if (altitude < 0) return 'rgba(0, 0, 0, 0)'; // Below horizon
    if (altitude < 15) return 'rgba(66, 135, 245, 0.5)'; // Low
    if (altitude < 30) return 'rgba(66, 135, 245, 0.6)';
    if (altitude < 45) return 'rgba(66, 135, 245, 0.7)';
    if (altitude < 60) return 'rgba(245, 193, 66, 0.7)';
    if (altitude < 75) return 'rgba(245, 124, 66, 0.8)';
    return 'rgba(245, 66, 66, 0.9)'; // Near zenith
  };

  const getAltitudeLabel = (altitude) => {
    if (altitude < 15) return 'Low (0-15°)';
    if (altitude < 30) return 'Low-Medium (15-30°)';
    if (altitude < 45) return 'Medium (30-45°)';
    if (altitude < 60) return 'Medium-High (45-60°)';
    if (altitude < 75) return 'High (60-75°)';
    return 'Overhead (75-90°)';
  };

  const handleHoursChange = useCallback((event, newValue) => {
    setHoursOffset(newValue);
    const now = new Date();
    const newDate = new Date(now.getTime() + newValue * 60 * 60 * 1000);
    setSelectedDate(newDate);
  }, []);

  const formatSliderLabel = (value) => {
    if (value === 0) return 'Now';
    if (value === 24) return '+24h';
    if (value === 48) return '+48h';
    return `+${value}h`;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Brightness3 sx={{ fontSize: 40, color: '#f6b93b' }} />
          <Box>
            <Typography variant="h4">Moon Position Overlay</Typography>
            <Typography variant="body2" color="text.secondary">
              Precise georeferenced moon position data across the United States
            </Typography>
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Control Panel */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Resolution</InputLabel>
              <Select
                value={resolution}
                label="Resolution"
                onChange={(e) => setResolution(e.target.value)}
              >
                <MenuItem value="low">Low (~150 points)</MenuItem>
                <MenuItem value="medium">Medium (~600 points)</MenuItem>
                <MenuItem value="high">High (~2400 points)</MenuItem>
                <MenuItem value="ultra">Ultra (~9600 points)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>View Mode</InputLabel>
              <Select
                value={viewMode}
                label="View Mode"
                onChange={(e) => setViewMode(e.target.value)}
              >
                <MenuItem value="heatmap">Heat Map (Circles)</MenuItem>
                <MenuItem value="markers">Markers</MenuItem>
                <MenuItem value="zones">Elevation Zones</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControlLabel
                control={
                <Switch
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                />
                }
                label="Auto-refresh every minute"
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2, px: 2 }}>
          <Typography variant="body2" gutterBottom>
            Time: {moment(selectedDate).format('MMM D, h:mm A')}
          </Typography>
          <Slider
            value={hoursOffset}
            onChange={handleHoursChange}
            min={0}
            max={48}
            step={1}
            marks={[
              { value: 0, label: 'Now' },
              { value: 12, label: '+12h' },
              { value: 24, label: '+24h' },
              { value: 36, label: '+36h' },
              { value: 48, label: '+48h' }
            ]}
            valueLabelDisplay="auto"
            valueLabelFormat={formatSliderLabel}
          />
        </Box>

      </Paper>

      {/* Moon Info Panel */}
      {moonData && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <Brightness3 sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Moon Phase
                  </Typography>
                  <Typography variant="h4" sx={{ color: PHASE_COLORS[moonData.moonPhase] }}>
                    {moonData.moonPhase}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {(moonData.illumination.fraction * 100).toFixed(1)}% Illuminated
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <Timeline sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Observation Time
                  </Typography>
                  <Typography variant="body1">
                    {moment(moonData.date).format('MMMM D, YYYY')}
                  </Typography>
                  <Typography variant="h6">
                    {moment(moonData.date).format('h:mm A z')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <Public sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Visibility
                  </Typography>
                  <Typography variant="h4">
                    {moonData.pointCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Grid points where moon is visible
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Selected Location Info */}
      {selectedLocation && locationData && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            <LocationOn sx={{ mr: 1, verticalAlign: 'middle' }} />
            Selected Location: {selectedLocation.lat.toFixed(4)}°, {selectedLocation.lng.toFixed(4)}°
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="text.secondary">Altitude</Typography>
              <Typography variant="h6">{locationData.position.altitude.toFixed(2)}°</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="text.secondary">Azimuth</Typography>
              <Typography variant="h6">{locationData.position.azimuth.toFixed(2)}°</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="text.secondary">Rise Time</Typography>
              <Typography variant="body1">
                {locationData.times.rise ? moment(locationData.times.rise).format('h:mm A') : 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="text.secondary">Set Time</Typography>
              <Typography variant="body1">
                {locationData.times.set ? moment(locationData.times.set).format('h:mm A') : 'N/A'}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Map */}
      <Paper sx={{ p: 0, overflow: 'hidden', height: 'calc(100vh - 250px)', minHeight: '600px' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '600px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <MapContainer
            center={defaultCenter}
            zoom={4}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapClickHandler onMapClick={handleMapClick} />

            {moonData && moonData.gridData && viewMode === 'heatmap' && (
              (() => {
                // Calculate grid cell size based on resolution
                const bounds = moonData.bounds;
                const resolutions = {
                  low: { lat: 25, lng: 60 },
                  medium: { lat: 50, lng: 120 },
                  high: { lat: 100, lng: 240 },
                  ultra: { lat: 200, lng: 480 }
                };
                const gridRes = resolutions[moonData.resolution] || resolutions.medium;
                const latStep = (bounds.north - bounds.south) / gridRes.lat;
                const lngStep = (bounds.east - bounds.west) / gridRes.lng;
                
                return moonData.gridData.map((point, index) => {
                  // Create rectangular bounds for each grid cell
                  const bounds = [
                    [point.lat - latStep / 2, point.lng - lngStep / 2],
                    [point.lat + latStep / 2, point.lng + lngStep / 2]
                  ];
                  
                  // Extract RGB values from rgba color string
                  const colorStr = getAltitudeColor(point.altitude);
                  const match = colorStr.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
                  const fillColor = match ? `rgb(${match[1]}, ${match[2]}, ${match[3]})` : '#4287f5';
                  
                  return (
                    <Rectangle
                      key={index}
                      bounds={bounds}
                      pathOptions={{
                        fillColor: fillColor,
                        fillOpacity: 0.5,
                        weight: 0,
                        interactive: false
                      }}
                    />
                  );
                });
              })()
            )}

            {moonData && moonData.gridData && viewMode === 'markers' && (
              moonData.gridData
                .filter((_, index) => index % 5 === 0) // Show every 5th marker to avoid clutter
                .map((point, index) => {
                  // Extract RGB from color
                  const colorStr = getAltitudeColor(point.altitude);
                  const match = colorStr.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
                  const fillColor = match ? `rgb(${match[1]}, ${match[2]}, ${match[3]})` : '#4287f5';
                  
                  const customIcon = new L.CircleMarker([point.lat, point.lng], {
                    radius: 4,
                    fillColor: fillColor,
                    fillOpacity: 0.8,
                    weight: 1,
                    color: '#ffffff'
                  });
                  
                  return (
                    <Marker
                      key={index}
                      position={[point.lat, point.lng]}
                      title={`Alt: ${point.altitude.toFixed(1)}° | Az: ${point.azimuth.toFixed(1)}°`}
                    />
                  );
                })
            )}

            {selectedLocation && (
              <Marker
                position={[selectedLocation.lat, selectedLocation.lng]}
              />
            )}
          </MapContainer>
        )}
      </Paper>

      {/* Legend */}
      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Moon Altitude Legend
        </Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap">
          {[
            { label: 'Low (0-15°)', color: 'rgba(66, 135, 245, 0.5)' },
            { label: 'Low-Med (15-30°)', color: 'rgba(66, 135, 245, 0.6)' },
            { label: 'Medium (30-45°)', color: 'rgba(66, 135, 245, 0.7)' },
            { label: 'Med-High (45-60°)', color: 'rgba(245, 193, 66, 0.7)' },
            { label: 'High (60-75°)', color: 'rgba(245, 124, 66, 0.8)' },
            { label: 'Overhead (75-90°)', color: 'rgba(245, 66, 66, 0.9)' }
          ].map((item) => (
            <Chip
              key={item.label}
              label={item.label}
              sx={{
                backgroundColor: item.color,
                border: '1px solid #666',
                color: '#fff',
                fontWeight: 'bold'
              }}
            />
          ))}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Click anywhere on the map to see detailed moon position data for that location
        </Typography>
      </Paper>
    </Box>
  );
}

export default MoonPosition;
